// src/store/studyMaterialsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized axios instance

// Fetch study materials
export const fetchStudyMaterials = createAsyncThunk(
  "studyMaterials/fetchStudyMaterials",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/study-materials/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update study material status
export const updateStudyMaterialStatus = createAsyncThunk(
  "studyMaterials/updateStudyMaterialStatus",
  async ({ id, SMStatus }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/study-materials/${id}`, { SMStatus });
      return { id, SMStatus: response.data.SMStatus };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete study material
export const deleteStudyMaterial = createAsyncThunk(
  "studyMaterials/deleteStudyMaterial",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/study-materials/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert new study material
export const insertStudyMaterial = createAsyncThunk(
  "studyMaterials/insertStudyMaterial",
  async (material, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/study-materials/", material);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Edit study material
export const editStudyMaterial = createAsyncThunk(
  "studyMaterials/editStudyMaterial",
  async (material, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/study-materials/${material.id}/`, material);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const studyMaterialsSlice = createSlice({
  name: "studyMaterials",
  initialState: {
    studyMaterials: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudyMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudyMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.studyMaterials = action.payload;
      })
      .addCase(fetchStudyMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateStudyMaterialStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudyMaterialStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, SMStatus } = action.payload;
        const index = state.studyMaterials.findIndex((material) => material.id === id);
        if (index !== -1) {
          state.studyMaterials[index].SMStatus = SMStatus;
        }
      })
      .addCase(updateStudyMaterialStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteStudyMaterial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudyMaterial.fulfilled, (state, action) => {
        state.loading = false;
        state.studyMaterials = state.studyMaterials.filter(
          (material) => material.id !== action.payload
        );
      })
      .addCase(deleteStudyMaterial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertStudyMaterial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertStudyMaterial.fulfilled, (state, action) => {
        state.loading = false;
        state.studyMaterials.push(action.payload);
      })
      .addCase(insertStudyMaterial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editStudyMaterial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editStudyMaterial.fulfilled, (state, action) => {
        state.loading = false;
        const updatedMaterial = action.payload;
        const index = state.studyMaterials.findIndex(
          (material) => material.id === updatedMaterial.id
        );
        if (index !== -1) {
          state.studyMaterials[index] = updatedMaterial;
        }
      })
      .addCase(editStudyMaterial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default studyMaterialsSlice.reducer;
